import { queryOptions, useQueries, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  confirmOepDocument,
  getOepDocumentProcessingStatus,
  getOepDocumentUploadUrl,
  type OepDocumentListItemDto,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'
import {
  OEP_UPLOAD_CONTENT_TYPES,
  resolveOepUploadExtension,
  validateOepUploadFile,
} from '../oepUploadFile.schema'
import { MIN_OEP_YEAR, type OepUploadState } from '../types'
import { resolveRefetchInterval } from './useOepDocumentProcessingStatus'

const LIST_RELOAD_INTERVAL_MS = 15_000
const LIST_RELOAD_MAX_ATTEMPTS = 8

interface Options {
  readonly clientId: string
  // The client's OEP plan year — MUST match the year the approval flow reads
  // (policy.planYear), not the calendar year, or uploads won't line up there.
  readonly oepYear: number
}

interface Result {
  readonly uploadState: OepUploadState
  readonly inlineError: string | null
  readonly handleFile: (file: File) => Promise<void>
  // Files uploaded in this session, as list rows. The list leaves out an
  // upload until processing sets its doc type, and never shows one whose
  // processing failed before that, so the table shows these rows until the
  // list returns them (EMS-11022).
  readonly pendingDocuments: readonly OepDocumentListItemDto[]
  // Drops pending rows the list now returns, or that were deleted.
  readonly forgetPendingDocuments: (documentIds: readonly string[]) => void
}

interface PendingUpload {
  readonly documentId: string
  readonly filename: string
  readonly uploadedAt: string
  readonly fileSizeBytes: number
}

function toPendingRow(
  upload: PendingUpload,
  processingStatus: OepDocumentListItemDto['processingStatus']
): OepDocumentListItemDto {
  return {
    documentId: upload.documentId,
    filename: upload.filename,
    version: 0,
    fileSizeBytes: upload.fileSizeBytes,
    uploadedBy: null,
    uploadedAt: upload.uploadedAt,
    status: false,
    processingStatus,
    completedAt: null,
    failureReason: null,
    validationIssues: null,
    contributionFileUrl: null,
    useCurrentModel: null,
    adjustmentsMade: null,
    hasErrorFile: false,
    errorMessage: null,
    hasGenerationErrors: false,
    docType: null,
    analysisJobId: null,
  }
}

/**
 * Uploads one analysis file (EMS-10154 FR-013: a single always-available
 * "Upload" action, no "Replace" relabeling — multiple non-deleted files now
 * coexist per plan year). The Analysis Files table itself is sourced from
 * `useOepDocumentList`, not this hook; a successful upload invalidates that
 * list query plus the client-scoped approval document cache so both refetch
 * without a page reload.
 */
export function useOepDocumentUpload({ clientId, oepYear }: Options): Result {
  const queryClient = useQueryClient()

  const [uploadState, setUploadState] = useState<OepUploadState>('idle')
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [pendingUploads, setPendingUploads] = useState<
    readonly PendingUpload[]
  >([])

  // Same query key as useOepDocumentProcessingStatus, so a row's own poll and
  // this one share one request.
  const statusQueries = useQueries({
    queries: pendingUploads.map((upload) =>
      queryOptions({
        queryKey: APPROVAL_QUERY_KEYS.oepDocumentProcessingStatus(
          clientId,
          upload.documentId
        ),
        queryFn: () =>
          getOepDocumentProcessingStatus(clientId, upload.documentId),
        refetchInterval: (query) =>
          resolveRefetchInterval(
            query.state.data,
            query.state.errorUpdateCount
          ),
      })
    ),
  })

  // When a pending upload finishes processing, reload the list so it picks up
  // the row (with its doc type) if the backend now returns it.
  const finishedUploadIdsRef = useRef(new Set<string>())
  const finishedUploadKey = pendingUploads
    .filter((_, index) => {
      const status = statusQueries[index]?.data?.processingStatus
      return status === 'completed' || status === 'failed'
    })
    .map((upload) => upload.documentId)
    .join(',')
  useEffect(() => {
    const newlyFinished = finishedUploadKey
      .split(',')
      .filter(
        (documentId) =>
          documentId !== '' && !finishedUploadIdsRef.current.has(documentId)
      )
    if (newlyFinished.length === 0) return
    newlyFinished.forEach((documentId) =>
      finishedUploadIdsRef.current.add(documentId)
    )
    void queryClient.invalidateQueries({
      queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
    })
  }, [finishedUploadKey, queryClient, clientId])

  // A completed upload can still be missing from that reload (its doc type is
  // not set yet), so keep reloading the list for a while until it shows up
  // and forgetPendingDocuments drops the pending row.
  const completedUploadKey = pendingUploads
    .filter(
      (_, index) => statusQueries[index]?.data?.processingStatus === 'completed'
    )
    .map((upload) => upload.documentId)
    .join(',')
  useEffect(() => {
    if (completedUploadKey === '') return undefined
    let attempts = 0
    const timer = setInterval(() => {
      attempts += 1
      if (attempts >= LIST_RELOAD_MAX_ATTEMPTS) clearInterval(timer)
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
      })
    }, LIST_RELOAD_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [completedUploadKey, queryClient, clientId])

  const pendingDocuments = pendingUploads.map((upload, index) =>
    toPendingRow(
      upload,
      statusQueries[index]?.data?.processingStatus ?? 'processing'
    )
  )

  const forgetPendingDocuments = useCallback(
    (documentIds: readonly string[]) => {
      if (documentIds.length === 0) return
      setPendingUploads((current) => {
        const next = current.filter(
          (upload) => !documentIds.includes(upload.documentId)
        )
        return next.length === current.length ? current : next
      })
    },
    []
  )

  const invalidateAfterUpload = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
    })
    void queryClient.invalidateQueries({
      queryKey: APPROVAL_QUERY_KEYS.oepDocumentByClient(clientId),
    })
  }, [queryClient, clientId])

  const handleFile = useCallback(
    async (file: File) => {
      setInlineError(null)

      if (oepYear < MIN_OEP_YEAR) {
        setInlineError('Plan year is not available yet. Please try again.')
        return
      }
      const fileError = validateOepUploadFile(file)
      if (fileError !== null) {
        setInlineError(fileError)
        return
      }

      setUploadState('presigning')

      try {
        const presign = await getOepDocumentUploadUrl(
          clientId,
          oepYear,
          file.name
        )

        setUploadState('uploading')
        const extension = resolveOepUploadExtension(file.name)
        const contentType =
          extension !== null ? OEP_UPLOAD_CONTENT_TYPES[extension] : undefined
        const putRes = await fetch(presign.presignedUrl, {
          method: 'PUT',
          body: file,
          headers:
            contentType !== undefined ? { 'Content-Type': contentType } : {},
        })
        if (!putRes.ok) {
          throw new Error(`S3 upload failed (${putRes.status})`)
        }

        setUploadState('saving')
        const confirmed = await confirmOepDocument(clientId, {
          oepYear,
          uploadKey: presign.uploadKey,
          filename: file.name,
        })
        setPendingUploads((current) => [
          {
            documentId: confirmed.documentId,
            filename: confirmed.filename,
            uploadedAt: confirmed.uploadedAt ?? dayjs().toISOString(),
            fileSizeBytes: file.size,
          },
          ...current,
        ])
        setUploadState('success')
        invalidateAfterUpload()
      } catch (err: unknown) {
        const msg =
          err instanceof Error
            ? err.message
            : 'Upload failed. Please try again.'
        setInlineError(msg)
        setUploadState('error')
      }
    },
    [clientId, oepYear, invalidateAfterUpload]
  )

  return {
    uploadState,
    inlineError,
    handleFile,
    pendingDocuments,
    forgetPendingDocuments,
  }
}
